import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDateTime } from "@/lib/helpers";
import { MapPin, Shuffle, ShieldAlert } from "lucide-react";

// Schedule -> lesson/class detail dialog (view details, change meeting
// location, mark status, cancel). Extracted from Schedule.js (props-driven,
// no behavior change).
export const LessonDetailDialog = ({
  selectedEvent,
  onOpenChange,
  isStaff,
  overrideLocation,
  onOverrideLocationChange,
  activeLocations,
  onSaveLocationOverride,
  withinCancellationWindow,
  businessRules,
  waiveFee,
  onWaiveFeeChange,
  onMarkStatus,
  onCancelAppointment,
}) => (
  <Dialog open={!!selectedEvent} onOpenChange={(v) => !v && onOpenChange(v)}>
    <DialogContent data-testid="lesson-detail-dialog">
      {selectedEvent && (
        <>
          <DialogHeader>
            <DialogTitle>{selectedEvent.resource.type === "lesson" ? selectedEvent.resource.student_name : selectedEvent.resource.classroom_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p><span className="text-muted-foreground">When: </span>{formatDateTime(selectedEvent.resource.start_time)} - {formatDateTime(selectedEvent.resource.end_time)}</p>
            <p><span className="text-muted-foreground">Instructor: </span>{selectedEvent.resource.instructor_name}</p>
            {selectedEvent.resource.vehicle_name && <p><span className="text-muted-foreground">Vehicle: </span>{selectedEvent.resource.vehicle_name}</p>}
            <p><span className="text-muted-foreground">Status: </span><StatusBadge status={selectedEvent.resource.status} /></p>
            {selectedEvent.resource.effective_meeting_location && (
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-muted-foreground">Meeting location: </span>
                <Badge variant="outline" className="inline-flex items-center gap-1" data-testid="lesson-detail-meeting-location-chip">
                  <MapPin className="h-3 w-3" /> {selectedEvent.resource.effective_meeting_location}
                </Badge>
                {selectedEvent.resource.location_overridden && (
                  <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800 gap-1" data-testid="lesson-detail-location-override-indicator">
                    <Shuffle className="h-3 w-3" /> Overridden for this lesson
                  </Badge>
                )}
              </div>
            )}
            {selectedEvent.resource.notes && <p><span className="text-muted-foreground">Notes: </span>{selectedEvent.resource.notes}</p>}
            {isStaff && selectedEvent.resource.type === "lesson" && (
              <div className="flex items-center gap-2 pt-1">
                <select
                  className="h-10 rounded-[var(--radius)] border bg-background px-2 text-sm flex-1"
                  value={overrideLocation}
                  onChange={(e) => onOverrideLocationChange(e.target.value)}
                  data-testid="lesson-detail-override-location-select"
                >
                  <option value="">Change meeting location...</option>
                  {activeLocations.map((l) => (
                    <option key={l.id} value={l.name}>{l.name}</option>
                  ))}
                </select>
                <Button size="sm" variant="outline" disabled={!overrideLocation} onClick={onSaveLocationOverride} data-testid="lesson-detail-save-override-button">
                  Save
                </Button>
              </div>
            )}
            {isStaff && withinCancellationWindow && selectedEvent.resource.status !== "cancelled" && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 mt-1" data-testid="cancellation-fee-warning">
                <ShieldAlert className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                <div className="flex-1 space-y-1.5">
                  <p className="text-xs text-amber-800">
                    Cancelling now is within the {businessRules?.cancellation_window_hours}-hour window — a ${businessRules?.cancellation_fee_amount?.toFixed?.(2) ?? businessRules?.cancellation_fee_amount} fee will be applied automatically.
                  </p>
                  <label className="flex items-center gap-2 text-xs text-amber-900">
                    <Checkbox checked={waiveFee} onCheckedChange={(v) => onWaiveFeeChange(!!v)} data-testid="waive-cancellation-fee-checkbox" />
                    Waive this fee (goodwill exception)
                  </label>
                </div>
              </div>
            )}
          </div>
          {isStaff && (
            <DialogFooter className="flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => onMarkStatus("completed")} data-testid="lesson-detail-complete-button">Complete</Button>
              <Button size="sm" variant="outline" onClick={() => onMarkStatus("no_show")} data-testid="lesson-detail-no-show-button">No Show</Button>
              <Button size="sm" variant="destructive" onClick={onCancelAppointment} data-testid="lesson-detail-cancel-button">Cancel</Button>
            </DialogFooter>
          )}
        </>
      )}
    </DialogContent>
  </Dialog>
);
